import { useEffect, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';

import { SearchIcon } from '../../assets/Icon';
import useAPI from '../../hooks/useAPI';
import { dateFormate } from '../../lib/constant';
import { UserTransactionResponse } from '../../lib/types/userTransactionHistory';
import { formatCurrency } from '../../utility/number.utils';
import CustomDateRangePicker from '../common/DatePicker/CustomDateRangePicker';
import TableComponent from '../Table';
import TransactionLink from '../ui/TransactionLink';
import apiInstance from '../../config/axios';
import { convertToCSV } from '../../lib/lib';
import styles from './UserTransferHistory.module.scss';

interface IPaginationObject {
  limit: number;
  page: number;
  search: string;
  from: string;
  to: string;
}

const MAX_DATE_RANGE_DAYS = 90;

// Helper function to format status text from SNAKE_CASE to Title Case
const formatStatusText = (status: string): string => {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to determine status color
const getStatusColor = (status: string): string => {
  if (status === 'SUCCESS') {
    return 'rgba(0, 138, 53, 1)'; // green
  } else if (status === 'FAILED') {
    return 'rgba(255, 0, 0, 1)'; // red
  } else if (status === 'PENDING') {
    return 'rgba(255, 193, 7, 1)'; // yellow
  } else {
    // All other statuses (ADD_CASH_INITIATED, etc.) show in warning/yellow
    return 'rgba(255, 193, 7, 1)'; // yellow
  }
};

export default function UserTransferHistory() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const [isDownloading, setIsDownloading] = useState(false);

  // Pagination and filter states
  const [paginationData, setPaginationData] = useState<IPaginationObject>({
    page: 1,
    limit: 10,
    search: '',
    from: '',
    to: '',
  });

  // Date range states
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [dateError, setDateError] = useState<string>('');

  // Search debounce
  const [searchInput, setSearchInput] = useState('');

  // Build API URL with query parameters
  const buildApiUrl = () => {
    const params = new URLSearchParams();
    params.append('page', String(paginationData.page));
    params.append('limit', String(paginationData.limit));
    if (paginationData.search) {
      params.append('search', paginationData.search);
    }
    if (paginationData.from) {
      params.append('fromDate', paginationData.from);
    }
    if (paginationData.to) {
      params.append('toDate', paginationData.to);
    }
    return `/token/user/transfer/history/${id}?${params.toString()}`;
  };

  // Fetch transfer history using useAPI
  const { data: transferHistoryResponse, isLoading } = useAPI<
    void,
    UserTransactionResponse
  >({
    url: buildApiUrl(),
    method: 'GET',
    enabled: !!id,
    dependencies: [
      id,
      paginationData.page,
      paginationData.limit,
      paginationData.search,
      paginationData.from,
      paginationData.to,
    ],
    onSuccess: () => {},
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch transfer history',
      );
    },
  });

  const transferData = transferHistoryResponse?.result?.Tokens || [];
  const totalCount = transferHistoryResponse?.result?.totalToken || 0;

  // Validate date range (max 90 days)
  const validateDateRange = (
    start: Date | undefined,
    end: Date | undefined,
  ): boolean => {
    if (!start || !end) return true;

    const diffInDays = moment(end).diff(moment(start), 'days');
    if (diffInDays > MAX_DATE_RANGE_DAYS) {
      setDateError(`Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`);
      return false;
    }
    setDateError('');
    return true;
  };

  // Handle date range change with validation
  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date);
    if (startDate && date) {
      if (validateDateRange(startDate, date)) {
        setPaginationData(prev => ({
          ...prev,
          page: 1,
          from: moment(startDate).format('YYYY-MM-DD'),
          to: moment(date).format('YYYY-MM-DD'),
        }));
      }
    } else if (!date) {
      setDateError('');
      setPaginationData(prev => ({
        ...prev,
        page: 1,
        from: '',
        to: '',
      }));
    }
  };

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== paginationData.search) {
        setPaginationData(prev => ({
          ...prev,
          page: 1,
          search: searchInput,
        }));
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Handle pagination change from TableComponent
  const handleFetchMoreData = (pageSize: number, page: number) => {
    setPaginationData(prev => ({
      ...prev,
      limit: pageSize,
      page: page,
    }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchInput('');
    setDateRange([null, null]);
    setStartDate(undefined);
    setEndDate(undefined);
    setDateError('');
    setPaginationData({
      page: 1,
      limit: 10,
      search: '',
      from: '',
      to: '',
    });
  };

  const columns = [
    {
      label: 'Employee/Group Name',
      id: 'name',
      renderCell: (row: any) => {
        return (
          <div>
            <span>
              {row?.receiverByFirstName && row?.receiverByLastName
                ? row?.receiverByFirstName + ' ' + row?.receiverByLastName
                : '-'}
            </span>
          </div>
        );
      },
    },
    {
      label: 'Transaction No.',
      id: 'transactionId',
      width: 300,
      renderCell: (row: any) => {
        return row?.transactionNo ? (
          <TransactionLink transactionNo={row?.transactionNo} />
        ) : (
          '-'
        );
      },
    },
    {
      label: 'Transferred Amount',
      id: 'tokenAmount',
      renderCell: (row: any) =>
        formatCurrency(row?.tokenAmount, { currency: '', decimals: 0 }),
    },
    // {
    //   label: 'Creator Email  ',
    //   id: 'createdByEmail',
    //   renderCell: (row: any) => row?.createdByEmail,
    // },
    // {
    //   label: 'Receiver Email',
    //   id: 'receiverEmail',
    //   renderCell: (row: any) => row?.receiverEmail,
    // },
    {
      label: 'Transaction Type',
      id: 'tokenType',
      renderCell: (row: any) => row?.tokenType || '-',
    },
    {
      label: 'Transaction Date & Time',
      id: 'transactionDate',
      renderCell: (row: any) =>
        row?.transactionDate ? dateFormate(row.transactionDate) : '-',
    },
    {
      label: 'Status',
      id: 'status',
      renderCell: (row: any) => (
        <div
          style={{
            fontSize: '16px',
            fontWeight: '600',
            color: getStatusColor(row?.status),
          }}
        >
          {formatStatusText(row?.status) || '-'}
        </div>
      ),
    },
  ];

  const hasActiveFilters =
    paginationData.search || paginationData.from || paginationData.to;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const params = new URLSearchParams();
      params.append('fromDate', paginationData.from);
      params.append('toDate', paginationData.to);
      params.append('search', paginationData.search || '');

      const response = await apiInstance.get(
        `/token/user/transfer/history/download/${id}?${params.toString()}`,
      );

      const transactionHistory = response.data.result.Tokens;

      if (!transactionHistory || transactionHistory.length === 0) {
        toast.error('No data available to download');
        return;
      }

      const csvContent = convertToCSV(transactionHistory);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = moment().format('DDMMYYYYHHmmss');
      link.setAttribute('download', `UserTransferHistory${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download error:', error);
      toast.error(error?.response?.data?.message || 'Failed to download CSV');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="bg-white rounded-3 p-3">
      {/* Filters Section */}
      <div className="d-flex flex-wrap gap-3 mb-3 align-items-start">
        {/* Search Input */}
        <div
          className="flex-grow-1"
          style={{ minWidth: '180px', maxWidth: '250px' }}
        >
          <Form.Label className="small fw-semibold mb-1">Search</Form.Label>
          <InputGroup size="sm">
            <InputGroup.Text className="bg-white">
              <SearchIcon size={14} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search by name, email..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="border-start-0 custom-input"
              size="sm"
            />
          </InputGroup>
        </div>

        {/* Date Range Picker */}
        <div style={{ minWidth: '200px' }} className="d-flex flex-column">
          <Form.Label className="small fw-semibold mb-1">
            Transaction Date{' '}
            <span
              className="text-muted fw-normal"
              style={{ fontSize: '11px' }}
            ></span>
          </Form.Label>
          <CustomDateRangePicker
            dateRange={dateRange}
            setDateRange={setDateRange}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            // @ts-ignore
            setEndDate={handleEndDateChange}
            placeholder="Select date range"
          />
          {dateError && (
            <Form.Text className="text-danger small">{dateError}</Form.Text>
          )}
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div className="d-flex flex-column">
            <Form.Label className="small fw-semibold mb-1 custom-filter">
              {/* <span className="text-muted fw-normal" style={{ fontSize: '11px' }}>
              
            </span> */}
            </Form.Label>

            <Button
              variant="outline-danger"
              size="sm"
              onClick={handleClearFilters}
              className="rounded-2 custom-filter-button"
            >
              Clear Filters
            </Button>
          </div>
        )}

        {/* Download Button */}
        <div className="d-flex flex-column">
          <Form.Label className="small fw-semibold mb-1">&nbsp;</Form.Label>
          <Button
            className={styles.actionButton}
            onClick={handleDownload}
            disabled={isDownloading}
          >
            {isDownloading ? 'Downloading...' : 'Download CSV'}
          </Button>
        </div>
      </div>

      {/* Transfer History Table */}
      <TableComponent
        data={transferData}
        columns={columns}
        totalCount={totalCount}
        fetchMore={handleFetchMoreData}
        isServerSidePagination
        perPageSize={paginationData.limit}
        activePage={paginationData.page}
        loading={isLoading}
        customBodyCss="custom-table-user-transfer-history"
      />
    </div>
  );
}
